import React, { useState, useEffect, useRef } from "react";
import {
  RefreshCw,
  CheckCircle,
  Clock,
  XCircle,
  FileText,
  Settings,
  Database,
  Terminal,
} from "lucide-react";
import apiService from "../../services/api";
import { Header } from '../../components/datasync/Header/Header';
import { LoadingSpinner } from "../../components/common";

import { 
  getDefaultDateRange, 
  getCurrentCycleMonth,
  setDocumentMeta, 
  refreshDashboardCache 
} from '../../utils/dashboardUtils';
import { 
  DEFAULT_SECTION_STATE, 
  CACHE_ENDPOINTS, 
  DOCUMENT_TITLES 
} from '../../utils/dashboardConstants';



// Entity configuration
const ENTITY_CONFIGS = [
  { id: "TimeActivity", name: "Time Activities", apiEntity: "TimeActivity" },
  { id: "Invoice", name: "Invoices", apiEntity: "Invoice" },
  { id: "Payment", name: "Payments", apiEntity: "Payment" },
  { id: "Item", name: "Items", apiEntity: "Item" },
  { id: "Customer", name: "Customers", apiEntity: "Customer" },
  { id: "Account", name: "Accounts", apiEntity: "Account" },
];

// Reports configuration mapping
const REPORTS_CONFIG = [
  {
    id: "payments-invoices",
    name: "Payments & Invoices",
    description: "Track payment status and invoice aging",
    apiReportType: "PaymentsAndInvoices",
  },
  {
    id: "profit-loss",
    name: "Profit & Loss",
    description: "Income statement and financial performance",
    apiReportType: "ProfitAndLoss",
  },
  {
    id: "aggregation",
    name: "Aggregation",
    description: "Aggregated data analysis and insights",
    apiReportType: "Aggregation",
  },
];

const PipelineDashboard = () => {
  const [pipelines, setPipelines] = useState([]);
  const [reports, setReports] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);
  const [syncing, setSyncing] = useState({});
  const [syncingReports, setSyncingReports] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [qbConnected, setQbConnected] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetails, setTaskDetails] = useState(null);
  const [loadingTaskDetails, setLoadingTaskDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [syncSettings, setSyncSettings] = useState({
    sync_mode: "incremental",
    auto_sync_enabled: true,
    sync_frequency: "After 3 Days",
    start_date: "2024-01-01",
    end_date: "2026-12-31",
    notify_on_complete: false,
    notify_on_failure: true,
    bigquery_enabled: true,
  });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [nextScheduledRun, setNextScheduledRun] = useState(null);
  const [loadingNextRun, setLoadingNextRun] = useState(false);
  const recordsPerPage = 7;

  // Mock data refresh state
  const [mockRefreshing, setMockRefreshing] = useState(false);
  const [mockProgress, setMockProgress] = useState(0);
  const [mockLogs, setMockLogs] = useState([]);
  const [showMockPanel, setShowMockPanel] = useState(false);
  const mockLogRef = useRef(null);

  // Auto-scroll mock logs
  useEffect(() => {
    if (mockLogRef.current) {
      mockLogRef.current.scrollTop = mockLogRef.current.scrollHeight;
    }
  }, [mockLogs]);

  const handleMockRefresh = async () => {
    setMockRefreshing(true);
    setMockProgress(0);
    setMockLogs([]);
    setShowMockPanel(true);

    try {
      const token = localStorage.getItem("auth_token");
      const baseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
      const response = await fetch(`${baseUrl}/api/mock/refresh`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const event = JSON.parse(line.slice(6));
              setMockProgress(event.progress >= 0 ? event.progress : 0);
              setMockLogs((prev) => [...prev, event]);

              if (event.status === "failed") {
                setMockRefreshing(false);
                return;
              }
            } catch (e) {
              // skip malformed events
            }
          }
        }
      }
    } catch (error) {
      setMockLogs((prev) => [
        ...prev,
        {
          step: "error",
          status: "failed",
          message: `Connection error: ${error.message}`,
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setMockRefreshing(false);
    }
  };

  // Fetch pipeline data on mount
  useEffect(() => {
    fetchPipelineData();
    fetchSyncHistory(currentPage);
    fetchSyncSettings();
    fetchNextScheduledRun();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchSyncSettings = async () => {
    try {
      setLoadingSettings(true);
      const response = await apiService.get("/api/sync/settings");

      if (response.settings) {
        setSyncSettings(response.settings);
      }
    } catch (err) {
      console.error("Failed to fetch sync settings:", err);
      // Keep default settings if fetch fails
    } finally {
      setLoadingSettings(false);
    }
  };

  const fetchNextScheduledRun = async () => {
    try {
      setLoadingNextRun(true);
      const response = await apiService.get("/api/sync/next-scheduled-run");
      // console.log("Next scheduled run response:", response);
      setNextScheduledRun(response);
    } catch (err) {
      console.error("Failed to fetch next scheduled run:", err);
      // Don't show error to user, just log it
      setNextScheduledRun(null);
    } finally {
      setLoadingNextRun(false);
    }
  };

  const saveSyncSettings = async () => {
    try {
      setSavingSettings(true);
      await apiService.post("/api/sync/settings", syncSettings);

      // Show success message
      alert("Settings saved successfully!");
      setShowSettingsModal(false);
      
      // Refresh next scheduled run after settings change
      await fetchNextScheduledRun();
    } catch (err) {
      console.error("Failed to save sync settings:", err);
      alert(`Failed to save settings: ${err.message || "Unknown error"}`);
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchPipelineData = async () => {
    try {
      setLoading(true);

      // Fetch all entities' latest sync data in one API call
      const response = await apiService.get("/api/qb/sync-tasks/latest-all");

      const entitySyncs =
        response.data?.entity_syncs || response.entity_syncs || {};
      const reportSyncs =
        response.data?.report_syncs || response.report_syncs || {};

      // Transform the response into pipeline data
      const pipelineData = ENTITY_CONFIGS.map((config) => {
        const syncData = entitySyncs[config.apiEntity];

        return {
          id: config.id,
          name: config.name,
          entity: config.apiEntity,
          lastSynced: syncData?.completed_at || null,
          recordCount: syncData?.records_saved || 0,
          status: syncData?.status || "no_data",
          taskId: syncData?._id || null,
        };
      });

      // Transform report syncs into report data
      const reportData = REPORTS_CONFIG.map((config) => {
        const syncData = reportSyncs[config.apiReportType];

        return {
          id: config.id,
          name: config.name,
          description: config.description,
          reportType: config.apiReportType,
          lastGenerated: syncData?.completed_at || null,
          recordCount: syncData?.records_saved || 0,
          status: syncData?.status || "no_data",
          taskId: syncData?._id || null,
        };
      });

      setPipelines(pipelineData);
      setReports(reportData);
    } catch (err) {
      // ...existing code...
      // Check if it's a QB connection issue
      if (
        err.status === 400 &&
        err.message &&
        err.message.includes("QuickBooks")
      ) {
        setQbConnected(false);
        setErrorMessage(err.message);
      }
    } finally {
      setLoading(false);
    }
  };
  const fetchSyncHistory = async (page = 1) => {
    try {
      setLoadingHistory(true);
      const response = await apiService.get("/api/qb/sync-tasks/history", {
        limit: recordsPerPage,
        skip: (page - 1) * recordsPerPage,
      });

      const tasks = response.data?.tasks || response.tasks || [];
      const total = response.data?.total || response.total || tasks.length;

      // Transform tasks to match the display format
      const formattedHistory = tasks.map((task) => ({
        id: task._id,
        status: task.status,
        timestamp: task.completed_at || task.started_at,
        duration: calculateDuration(task.started_at, task.completed_at),
        recordsSynced: task.records_saved || 0,
        entityType: task.entity_type,
        reportType: task.report_type,
        userName: task.user_name || "System",
        userEmail: task.user_email || "",
        errorMessage: task.error_message,
      }));

      setSyncHistory(formattedHistory);
      setTotalPages(Math.ceil(total / recordsPerPage));
    } catch (err) {
      // ...existing code...
    } finally {
      setLoadingHistory(false);
    }
  };

  const calculateDuration = (startedAt, completedAt) => {
    if (!startedAt || !completedAt) return "N/A";

    const start = new Date(startedAt);
    const end = new Date(completedAt);
    const durationMs = end - start;

    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };

  const pluralizeEntity = (entityType) => {
    const pluralMap = {
      TimeActivity: "Time Activities",
      Invoice: "Invoices",
      Payment: "Payments",
      Item: "Items",
      Customer: "Customers",
      Account: "Accounts",
    };
    return pluralMap[entityType] || entityType;
  };

  const formatEntityOrReport = (entityType, reportType) => {
    if (entityType) {
      return pluralizeEntity(entityType);
    } else if (reportType) {
      const reportNameMap = {
        PaymentsAndInvoices: "Payments & Invoices",
        ProfitAndLoss: "Profit & Loss",
        Aggregation: "Aggregation",
      };
      return reportNameMap[reportType] || reportType;
    }
    return "N/A";
  };

  const fetchTaskDetails = async (taskId) => {
    try {
      // Open modal immediately
      setSelectedTask(taskId);
      setLoadingTaskDetails(true);
      setTaskDetails(null); // Clear previous details

      const response = await apiService.get(`/api/qb/sync-tasks/${taskId}`);
      const details = response.data || response;
      setTaskDetails(details);
    } catch (err) {
      // ...existing code...
      alert(`Failed to load task details: ${err.message}`);
      closeModal(); // Close modal on error
    } finally {
      setLoadingTaskDetails(false);
    }
  };

  const closeModal = () => {
    setSelectedTask(null);
    setTaskDetails(null);
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatFullDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleSyncNow = async (pipelineId) => {
    setSyncing((prev) => ({ ...prev, [pipelineId]: true }));

    try {
      // Find the pipeline to get its entity name
      const pipeline = pipelines.find((p) => p.id === pipelineId);
      const entity = pipeline?.entity;

      // Call the sync API endpoint for this specific entity
      await apiService.post("/api/sync/data", {
        sync_mode: syncSettings.sync_mode,
        entities: [entity],
        start_date: syncSettings.start_date,
        end_date: syncSettings.end_date,
      });

      // Clear cache to force fresh data fetch
      apiService.clearCache("sync-tasks");

      // Refetch data on successful response
      await fetchPipelineData();
      await fetchSyncHistory(currentPage);
    } catch (error) {
      // ...existing code...
      alert(`Failed to start sync: ${error.message}`);
    } finally {
      // Clear syncing state
      setSyncing((prev) => {
        const newState = { ...prev };
        delete newState[pipelineId];
        return newState;
      });
    }
  };

  const handleSyncAll = async () => {
    pipelines.forEach((pipeline) => {
      setSyncing((prev) => ({ ...prev, [pipeline.id]: true }));
    });

    // Also set syncing state for all reports
    reports.forEach((report) => {
      setSyncingReports((prev) => ({ ...prev, [report.id]: true }));
    });

    try {
      // Get all entity types
      const allEntities = pipelines.map((p) => p.entity);

      // Get all report names
      const allReports = reports.map((r) => r.reportType);

      // Call the sync API endpoint with both entities and reports
      await apiService.post("/api/sync/data", {
        sync_mode: syncSettings.sync_mode,
        entities: allEntities,
        reports: allReports,
        start_date: syncSettings.start_date,
        end_date: syncSettings.end_date,
        auto_sync_enabled: syncSettings.auto_sync_enabled,
        sync_frequency: syncSettings.sync_frequency,
      });

      // Clear cache to force fresh data fetch
      apiService.clearCache("sync-tasks");

      // Refetch data on successful response
      await fetchPipelineData();
      await fetchSyncHistory(currentPage);
    } catch (error) {
      // ...existing code...
      alert(`Failed to start sync: ${error.message}`);
    } finally {
      // Clear all syncing states
      setSyncing({});
      setSyncingReports({});
    }
  };

  const handleSyncReport = async (reportId) => {
    setSyncingReports((prev) => ({ ...prev, [reportId]: true }));

    try {
      // Map report ID to backend report name
      const reportNameMap = {
        "payments-invoices": "PaymentsAndInvoices",
        "profit-loss": "ProfitAndLoss",
        aggregation: "Aggregation",
      };

      const reportName = reportNameMap[reportId];

      if (!reportName) {
        throw new Error(`Unknown report type: ${reportId}`);
      }

      // Call the sync API endpoint with report name
      await apiService.post("/api/sync/data", {
        sync_mode: syncSettings.sync_mode,
        entities: [],
        reports: [reportName],
        start_date: syncSettings.start_date,
        end_date: syncSettings.end_date,
      });

      // Clear cache to force fresh data fetch
      apiService.clearCache("sync-tasks");

      // Refetch data on successful response
      await fetchPipelineData();
      await fetchSyncHistory(currentPage);
    } catch (error) {
      // ...existing code...
      alert(`Failed to start report sync: ${error.message}`);
    } finally {
      // Clear syncing state for this report
      setSyncingReports((prev) => {
        const newState = { ...prev };
        delete newState[reportId];
        return newState;
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "success":
        return <CheckCircle className="h-5 w-5" style={{ color: "#27C28A" }} />;
      case "failed":
        return <XCircle className="h-5 w-5 text-red-600" />;
      case "no_data":
        return <Clock className="h-5 w-5 text-gray-400" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "success":
        return "Completed";
      case "failed":
        return "Failed";
      case "no_data":
        return "No Data";
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "#27C28A";
      case "failed":
        return "#EF4444";
      case "no_data":
        return "#9CA3AF";
      default:
        return "#9CA3AF";
    }
  };


  // ============================================================================
  // EFFECTS
  // ============================================================================
    
    // Set document title and favicon - use shared utility
    useEffect(() => {
      setDocumentMeta(DOCUMENT_TITLES.DATA_SYNC);
    }, []);

  // ============================================================================
  // ============================================================================


  if (loading) {
    return <LoadingSpinner />;
  }

  if (!qbConnected && errorMessage) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto pt-20 px-6">
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              QuickBooks Not Connected
            </h2>
            <p className="text-gray-600 mb-6">{errorMessage}</p>
            <button
              onClick={() => (window.location.href = "/settings")}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Connect QuickBooks
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="layout-container responsive-space">
        {/* Header */}
        <Header
          loading={loading}
          syncing={syncing}
          lastUpdated={pipelines}
          nextScheduledRun={nextScheduledRun}
          loadingNextRun={loadingNextRun}
          onSyncAll={handleSyncAll}
          onOpenSettings={() => setShowSettingsModal(true)}
        />

        <div className="responsive-space">
        {/* Mock Data Refresh */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5 text-indigo-600" />
              <h2 className="text-base font-semibold text-gray-900">
                Demo Data Refresh
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {showMockPanel && mockLogs.length > 0 && !mockRefreshing && (
                <button
                  onClick={() => setShowMockPanel(false)}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Hide Log
                </button>
              )}
              <button
                onClick={handleMockRefresh}
                disabled={mockRefreshing}
                className="flex items-center gap-2 px-4 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${mockRefreshing ? "animate-spin" : ""}`}
                />
                {mockRefreshing ? "Refreshing..." : "Refresh Demo Data"}
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Regenerate mock data from source tables. This will update all dashboards with fresh anonymized data.
          </p>

          {/* Progress bar */}
          {showMockPanel && (
            <>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                <div
                  className="h-2 rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: `${mockProgress}%`,
                    backgroundColor: mockLogs.some((l) => l.status === "failed")
                      ? "#EF4444"
                      : mockProgress === 100
                      ? "#27C28A"
                      : "#6366F1",
                  }}
                />
              </div>

              {/* Live log */}
              <div
                ref={mockLogRef}
                className="bg-gray-900 rounded-md p-3 max-h-56 overflow-y-auto font-mono text-xs"
              >
                {mockLogs.map((log, i) => (
                  <div key={i} className="flex items-start gap-2 py-0.5">
                    <span className="text-gray-500 shrink-0">
                      {log.timestamp
                        ? new Date(log.timestamp).toLocaleTimeString()
                        : ""}
                    </span>
                    <span
                      className={
                        log.status === "failed"
                          ? "text-red-400"
                          : log.status === "success"
                          ? "text-green-400"
                          : "text-gray-300"
                      }
                    >
                      {log.step === "table_done" ? "\u2713" : log.step === "error" ? "\u2717" : "\u25B6"}{" "}
                      {log.message}
                      {log.records ? ` (${log.records.toLocaleString()} records)` : ""}
                    </span>
                  </div>
                ))}
                {mockRefreshing && (
                  <div className="flex items-center gap-2 py-0.5 text-indigo-400">
                    <span className="animate-pulse">_</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Current Sync Status */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h2 className="text-base font-semibold text-gray-900 mb-3">
            Current Sync Status
          </h2>

          <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-md">
            <div className="flex items-center gap-3">
              {Object.keys(syncing).length > 0 ? (
                <>
                  <RefreshCw className="h-5 w-5 text-blue-600 animate-spin" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Sync in progress
                    </p>
                    <p className="text-xs text-gray-500">
                      {Object.keys(syncing).length} pipeline(s) syncing
                    </p>
                  </div>
                </>
              ) : pipelines.some((p) => p.status === "failed") ? (
                <>
                  <XCircle className="h-5 w-5 text-red-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Some pipelines have errors
                    </p>
                    <p className="text-xs text-gray-500">
                      {pipelines.filter((p) => p.status === "failed").length}{" "}
                      pipeline(s) failed
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <CheckCircle
                    className="h-5 w-5"
                    style={{ color: "#27C28A" }}
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      All pipelines synced
                    </p>
                    <p className="text-xs text-gray-500">
                      No active sync jobs running
                    </p>
                  </div>
                </>
              )}
            </div>
            <div className="text-xs text-gray-500">
              {syncHistory.length > 0 && syncHistory[0]?.timestamp ? (
                <>
                  Last sync:{" "}
                  {(() => {
                    const mostRecent = new Date(
                      syncHistory[0].timestamp
                    ).getTime();

                    const diffMs = Date.now() - mostRecent;
                    const diffMinutes = Math.floor(diffMs / (1000 * 60));
                    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

                    if (diffMinutes < 1) {
                      return "Just now";
                    } else if (diffMinutes < 60) {
                      return `${diffMinutes} minute${
                        diffMinutes !== 1 ? "s" : ""
                      } ago`;
                    } else if (diffHours < 24) {
                      return `${diffHours} hour${
                        diffHours !== 1 ? "s" : ""
                      } ago`;
                    } else {
                      return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
                    }
                  })()}
                </>
              ) : (
                "Never synced"
              )}
            </div>
          </div>
        </div>

        {/* Data Pipelines */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">
              Data Pipelines
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Manage individual data sources from QuickBooks
            </p>
          </div>

          <div className="divide-y divide-gray-200">
            {pipelines.map((pipeline) => (
              <div
                key={pipeline.id}
                className="px-4 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  {/* Left Section - Icon and Info */}
                  <div className="flex items-center gap-3 flex-1">
                    {getStatusIcon(pipeline.status)}
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {pipeline.name}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {pipeline.lastSynced ? (
                          <>
                            Last synced: {formatDate(pipeline.lastSynced)} •{" "}
                            {pipeline.recordCount.toLocaleString()} records
                          </>
                        ) : (
                          "Never synced"
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Right Section - Status and Button */}
                  <div className="flex items-center gap-4">
                    <span
                      className="text-xs font-medium"
                      style={{ color: getStatusColor(pipeline.status) }}
                    >
                      {getStatusText(pipeline.status)}
                    </span>
                    <button
                      onClick={() => handleSyncNow(pipeline.id)}
                      disabled={syncing[pipeline.id]}
                      className="flex items-center gap-2 px-4 py-1.5 bg-white text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw
                        className={`h-3.5 w-3.5 ${
                          syncing[pipeline.id] ? "animate-spin" : ""
                        }`}
                      />
                      {syncing[pipeline.id] ? "Syncing..." : "Sync Now"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reports */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">Reports</h2>
            <p className="text-xs text-gray-500 mt-1">
              Generate and download financial reports
            </p>
          </div>

          <div className="divide-y divide-gray-200">
            {reports.map((report) => (
              <div
                key={report.id}
                className="px-4 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  {/* Left Section - Icon and Info */}
                  <div className="flex items-center gap-3 flex-1">
                    {getStatusIcon(report.status)}
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {report.name}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {report.lastGenerated ? (
                          <>
                            Last generated: {formatDate(report.lastGenerated)} •{" "}
                            {report.recordCount.toLocaleString()} records
                          </>
                        ) : (
                          "Never generated"
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Right Section - Status and Button */}
                  <div className="flex items-center gap-4">
                    <span
                      className="text-xs font-medium"
                      style={{ color: getStatusColor(report.status) }}
                    >
                      {getStatusText(report.status)}
                    </span>
                    <button
                      onClick={() => handleSyncReport(report.id)}
                      disabled={syncingReports[report.id]}
                      className="flex items-center gap-2 px-4 py-1.5 bg-white text-purple-600 border border-purple-600 rounded-md hover:bg-purple-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw
                        className={`h-3.5 w-3.5 ${
                          syncingReports[report.id] ? "animate-spin" : ""
                        }`}
                      />
                      {syncingReports[report.id] ? "Syncing..." : "Sync Now"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sync Job History */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">
              Sync Job History
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              View past sync jobs and their logs
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entity / Report
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Records Synced
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Initiated By
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loadingHistory ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-4 py-12 text-center text-sm text-gray-500"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mb-3" />
                        <p>Loading sync history...</p>
                      </div>
                    </td>
                  </tr>
                ) : syncHistory.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-4 py-8 text-center text-sm text-gray-500"
                    >
                      No sync history available
                    </td>
                  </tr>
                ) : (
                  syncHistory.map((job) => (
                    <tr key={job.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {job.status === "success" ? (
                            <CheckCircle
                              className="h-4 w-4"
                              style={{ color: "#27C28A" }}
                            />
                          ) : job.status === "failed" ? (
                            <XCircle className="h-4 w-4 text-red-600" />
                          ) : (
                            <Clock className="h-4 w-4 text-gray-400" />
                          )}
                          <span className="text-xs text-gray-900 capitalize">
                            {job.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900">
                        {formatFullDate(job.timestamp)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900">
                        {formatEntityOrReport(job.entityType, job.reportType)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900">
                        {job.recordsSynced.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="text-xs font-medium text-gray-900">
                            {job.userName}
                          </span>
                          {job.userEmail && (
                            <span className="text-xs text-gray-500">
                              {job.userEmail}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {job.errorMessage ? (
                          <span
                            className="text-xs text-red-600"
                            title={job.errorMessage}
                          >
                            Error: {job.errorMessage.substring(0, 30)}...
                          </span>
                        ) : (
                          <button
                            onClick={() => fetchTaskDetails(job.id)}
                            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                          >
                            <FileText className="h-3.5 w-3.5" />
                            View Details
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          {!loadingHistory && syncHistory.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1 || loadingHistory}
                  className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages || loadingHistory}
                  className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Task Details Modal */}
      {selectedTask && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-lg font-semibold text-gray-900">
                Sync Task Details
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4">
              {loadingTaskDetails ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
                  <span className="ml-3 text-gray-600">Loading details...</span>
                </div>
              ) : taskDetails ? (
                <div className="space-y-4">
                  {/* Status and Entity */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Status
                      </label>
                      <div className="mt-1 flex items-center gap-2">
                        {taskDetails.status === "success" ? (
                          <CheckCircle
                            className="h-5 w-5"
                            style={{ color: "#27C28A" }}
                          />
                        ) : taskDetails.status === "failed" ? (
                          <XCircle className="h-5 w-5 text-red-600" />
                        ) : (
                          <Clock className="h-5 w-5 text-gray-400" />
                        )}
                        <span className="text-sm font-medium text-gray-900 capitalize">
                          {taskDetails.status}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Entity Type
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {taskDetails.entity_type
                          ? pluralizeEntity(taskDetails.entity_type)
                          : "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Sync Mode and Company ID */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Sync Mode
                      </label>
                      <p className="mt-1 text-sm text-gray-900 capitalize">
                        {taskDetails.sync_mode || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Company ID
                      </label>
                      <p className="mt-1 text-sm text-gray-900 font-mono">
                        {taskDetails.qb_company_id || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Timestamps */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Started At
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {taskDetails.started_at
                          ? formatFullDate(taskDetails.started_at)
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Completed At
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {taskDetails.completed_at
                          ? formatFullDate(taskDetails.completed_at)
                          : "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Date Range (if applicable) */}
                  {(taskDetails.start_date || taskDetails.end_date) && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Start Date
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {taskDetails.start_date || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          End Date
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {taskDetails.end_date || "N/A"}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Record Counts */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Records Fetched
                      </label>
                      <p className="mt-1 text-lg font-semibold text-gray-900">
                        {taskDetails.records_fetched?.toLocaleString() || 0}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Records Normalized
                      </label>
                      <p className="mt-1 text-lg font-semibold text-gray-900">
                        {taskDetails.records_normalized?.toLocaleString() || 0}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Records Saved
                      </label>
                      <p className="mt-1 text-lg font-semibold text-gray-900">
                        {taskDetails.records_saved?.toLocaleString() || 0}
                      </p>
                    </div>
                  </div>

                  {/* Line Items Count (for Invoices) */}
                  {taskDetails.line_items_count > 0 && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Line Items Count
                      </label>
                      <p className="mt-1 text-lg font-semibold text-gray-900">
                        {taskDetails.line_items_count.toLocaleString()}
                      </p>
                    </div>
                  )}

                  {/* BigQuery Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        BigQuery Enabled
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {taskDetails.bigquery_enabled ? "Yes" : "No"}
                      </p>
                    </div>
                    {taskDetails.dataset_id && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Dataset ID
                        </label>
                        <p className="mt-1 text-sm text-gray-900 font-mono">
                          {taskDetails.dataset_id}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* User Info */}
                  {taskDetails.user_name && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Initiated By
                      </label>
                      <div className="mt-1">
                        <p className="text-sm font-medium text-gray-900">
                          {taskDetails.user_name}
                        </p>
                        {taskDetails.user_email && (
                          <p className="text-sm text-gray-500">
                            {taskDetails.user_email}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {taskDetails.error_message && (
                    <div>
                      <label className="text-sm font-medium text-red-600">
                        Error Message
                      </label>
                      <p className="mt-1 text-sm text-red-600 bg-red-50 p-3 rounded-md">
                        {taskDetails.error_message}
                      </p>
                    </div>
                  )}

                  {/* Validation Errors */}
                  {taskDetails.validation_errors && (
                    <div>
                      <label className="text-sm font-medium text-yellow-600">
                        Validation Errors
                      </label>
                      <pre className="mt-1 text-xs text-yellow-700 bg-yellow-50 p-3 rounded-md overflow-x-auto">
                        {JSON.stringify(taskDetails.validation_errors, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Task ID */}
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Task ID
                    </label>
                    <p className="mt-1 text-xs text-gray-600 font-mono break-all">
                      {taskDetails._id}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">
                  No details available
                </p>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Data Sync Settings Modal */}
      {showSettingsModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowSettingsModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-3">
                <Settings className="h-6 w-6 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Data Sync Settings
                </h3>
              </div>
              <button
                onClick={() => setShowSettingsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4">
              {loadingSettings ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
                  <span className="ml-3 text-gray-600">
                    Loading settings...
                  </span>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Sync Mode Section */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      Sync Mode
                    </h4>
                    <div className="space-y-2">
                      <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="syncMode"
                          value="incremental"
                          checked={syncSettings.sync_mode === "incremental"}
                          onChange={(e) =>
                            setSyncSettings({
                              ...syncSettings,
                              sync_mode: e.target.value,
                            })
                          }
                          className="mt-1"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            Incremental Sync
                          </div>
                          <div className="text-xs text-gray-500">
                            Only sync new or updated records since the last sync
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="syncMode"
                          value="full"
                          checked={syncSettings.sync_mode === "full"}
                          onChange={(e) =>
                            setSyncSettings({
                              ...syncSettings,
                              sync_mode: e.target.value,
                            })
                          }
                          className="mt-1"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            Full Sync
                          </div>
                          <div className="text-xs text-gray-500">
                            Sync all records from QuickBooks (may take longer)
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Auto-Sync Settings */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      Auto-Sync Schedule
                    </h4>
                    <p className="text-xs text-gray-600 mb-3 p-2 bg-blue-50 rounded border border-blue-200">
                      <span className="font-medium">Note:</span> Auto Sync Schedule will be enabled with the chosen frequency when you run all pipelines using the "Sync All Pipelines" button.
                    </p>
                    <div className="space-y-3">
                      <label className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            Enable Auto-Sync
                          </div>
                          <div className="text-xs text-gray-500">
                            Automatically sync data at scheduled intervals
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={syncSettings.auto_sync_enabled}
                          onChange={(e) =>
                            setSyncSettings({
                              ...syncSettings,
                              auto_sync_enabled: e.target.checked,
                            })
                          }
                          className="h-5 w-5 text-blue-600 rounded"
                        />
                      </label>
                      <div className="pl-3">
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          Sync Frequency
                        </label>
                        <select
                          value={syncSettings.sync_frequency}
                          onChange={(e) =>
                            setSyncSettings({
                              ...syncSettings,
                              sync_frequency: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option>Every 12 hours</option>
                          <option>Daily</option>
                          <option>After 3 Days</option>
                          <option>Weekly</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Date Range Settings */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      Date Range (for Reports)
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={syncSettings.start_date}
                          onChange={(e) =>
                            setSyncSettings({
                              ...syncSettings,
                              start_date: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          End Date
                        </label>
                        <input
                          type="date"
                          value={syncSettings.end_date}
                          onChange={(e) =>
                            setSyncSettings({
                              ...syncSettings,
                              end_date: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Notification Settings */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      Notifications
                    </h4>
                    <div className="space-y-2">
                      <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={syncSettings.notify_on_complete}
                          onChange={(e) =>
                            setSyncSettings({
                              ...syncSettings,
                              notify_on_complete: e.target.checked,
                            })
                          }
                          className="h-4 w-4 text-blue-600 rounded"
                        />
                        <div className="text-sm text-gray-900">
                          Notify me when sync completes
                        </div>
                      </label>
                      <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={syncSettings.notify_on_failure}
                          onChange={(e) =>
                            setSyncSettings({
                              ...syncSettings,
                              notify_on_failure: e.target.checked,
                            })
                          }
                          className="h-4 w-4 text-blue-600 rounded"
                        />
                        <div className="text-sm text-gray-900">
                          Notify me when sync fails
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* BigQuery Settings */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      BigQuery Integration
                    </h4>
                    <label className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          Enable BigQuery Export
                        </div>
                        <div className="text-xs text-gray-500">
                          Automatically export synced data to BigQuery
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={syncSettings.bigquery_enabled}
                        onChange={(e) =>
                          setSyncSettings({
                            ...syncSettings,
                            bigquery_enabled: e.target.checked,
                          })
                        }
                        className="h-5 w-5 text-blue-600 rounded"
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={saveSyncSettings}
                disabled={savingSettings}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingSettings ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
      
    </div>
  );
};

export default PipelineDashboard;
